import { useEffect, useState } from 'react';
import {
	getLobOptions,
	ImportTypeLob,
	ImportTypeLobSchema,
	update,
} from '@/api/admin/lob';
import { getActiveLocations } from '@/api/admin/location';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { ComboboxSelect } from '@/components/ui/custom/combobox-select';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarIcon, Edit } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function UpdateImportTypeLob({
	importTypeId,
	importTypeLob,
}: {
	importTypeId: number;
	importTypeLob: ImportTypeLob;
}) {
	const [open, setOpen] = useState(false);

	return (
		<div>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button className="m-auto" variant="ghost" onClick={() => setOpen(true)}>
							<Edit size={16} />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>Update Lob</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			{open && (
				<UpdateSheet
					open={open}
					setOpen={setOpen}
					importTypeId={importTypeId}
					importTypeLob={importTypeLob}
				/>
			)}
		</div>
	);
}

function UpdateSheet({
	open,
	setOpen,
	importTypeId,
	importTypeLob,
}: {
	open: boolean;
	setOpen: (open: boolean) => void;
	importTypeId: number;
	importTypeLob: ImportTypeLob;
}) {
	const queryClient = useQueryClient();

	const form = useForm<ImportTypeLob>({
		resolver: zodResolver(ImportTypeLobSchema),
		defaultValues: importTypeLob,
		mode: 'all',
	});

	const { reset, watch } = form;

	const { data: locationOptions, isLoading: isLoadingLocationOptions } =
		useQuery({
			queryKey: [`location-options`],
			queryFn: () => getActiveLocations(),
			staleTime: 5 * 60 * 1000, // Cache for 5 minutes
		});

	const [initialCompLocId] = useState(importTypeLob.compLocId);
	const selectedCompLocId = watch('compLocId');
	const { data: lobOptions, isLoading: isLoadingLobOptions } = useQuery({
		queryKey: [`lob-options`, selectedCompLocId, importTypeLob.lobId],
		queryFn: () =>
			getLobOptions(importTypeId, selectedCompLocId!, importTypeLob.lobId),
		staleTime: 5 * 60 * 1000,
		enabled: !!selectedCompLocId,
	});

	useEffect(() => {
		if (selectedCompLocId !== initialCompLocId) {
			form.resetField('lobId');
		}
	}, [selectedCompLocId, initialCompLocId, form]);

	useEffect(() => {
		reset(importTypeLob);
	}, [importTypeLob, reset]);

	const { mutate } = useMutation({
		mutationFn: (updatedImportTypeLob: ImportTypeLob) =>
			update(updatedImportTypeLob),
		onSuccess: () => {
			toast.success(`Success! Lob has been successfully updated.`, {
				dismissible: true,
			});
			setOpen(false);

			//Invalidate queries to refresh data
			queryClient.invalidateQueries({
				queryKey: [`lob-${importTypeId}`],
			});
			queryClient.invalidateQueries({
				queryKey: [`lob-options`],
			});
		},
		onError: () => {
			toast.error(`Error! Lob could not be updated.`, {
				dismissible: true,
			});
		},
	});

	const onSubmit: SubmitHandler<ImportTypeLob> = (values) => mutate(values);

	return (
		<Sheet open={open} onOpenChange={setOpen}>
			<SheetContent>
				<SheetHeader>
					<SheetTitle>Update Lob</SheetTitle>
					<SheetDescription></SheetDescription>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)}>
						<div className="space-y-4">
							<FormField
								control={form.control}
								name="importTypeId"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Import Type ID</FormLabel>
										<FormControl>
											<Input {...field} value={field.value ?? ''} disabled={true} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="compLocId"
								render={({ field }) => (
									<ComboboxSelect
										field={field}
										options={locationOptions}
										valueKey="compLocId"
										displayKey="brandName"
										isLoading={isLoadingLocationOptions}
										label="Company Location"
										placeholder="Select company location..."
										searchPlaceholder="Search company location..."
										required={true}
										emptyMessage="No company location found."
										loadingMessage="Loading company locations..."
									/>
								)}
							/>
							<FormField
								control={form.control}
								name="lobId"
								render={({ field }) => (
									<ComboboxSelect
										field={field}
										options={lobOptions}
										valueKey="value"
										displayKey="label"
										isLoading={isLoadingLobOptions}
										label="Lob"
										placeholder={
											selectedCompLocId
												? 'Select lob...'
												: 'Select a company location first'
										}
										searchPlaceholder="Search lobs..."
										required={true}
										emptyMessage="No lob name found."
										loadingMessage="Loading lobs..."
										disabled={!selectedCompLocId}
									/>
								)}
							/>
							<FormField
								control={form.control}
								name="numInstallments"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Number of Installments</FormLabel>
										<FormControl>
											<Input
												type="number"
												{...field}
												value={field.value ?? ''}
												onChange={(e) =>
													field.onChange(e.target.value ? Number(e.target.value) : undefined)
												}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="effectiveDate"
								render={({ field }) => (
									<FormItem className="flex flex-col">
										<FormLabel>
											Effective Date <span className="text-red-500">*</span>
										</FormLabel>

										<Popover>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant={'outline'}
														className={`w-full pl-3 text-left font-normal ${
															!field.value && 'text-muted-foreground'
														}`}
													>
														{field.value ? (
															new Date(field.value).toLocaleDateString()
														) : (
															<span>Pick a date</span>
														)}
														<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>

											<PopoverContent className="w-auto p-0">
												<Calendar
													mode="single"
													selected={field.value ? new Date(field.value) : undefined}
													onSelect={(date) => field.onChange(date)}
													captionLayout="dropdown"
													endMonth={new Date(new Date().getFullYear() + 2, 11)}
												/>
											</PopoverContent>
										</Popover>

										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="expirationDate"
								render={({ field }) => (
									<FormItem className="flex flex-col">
										<FormLabel>Expiration Date</FormLabel>

										<Popover>
											<PopoverTrigger asChild>
												<FormControl>
													<Button
														variant={'outline'}
														className={`w-full pl-3 text-left font-normal ${
															!field.value && 'text-muted-foreground'
														}`}
													>
														{field.value ? (
															new Date(field.value).toLocaleDateString()
														) : (
															<span>Pick a date</span>
														)}
														<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
													</Button>
												</FormControl>
											</PopoverTrigger>

											<PopoverContent className="w-auto p-0">
												<Calendar
													mode="single"
													selected={field.value ? new Date(field.value) : undefined}
													onSelect={(date) => field.onChange(date)}
													captionLayout="dropdown"
													endMonth={new Date(new Date().getFullYear() + 2, 11)}
												/>
											</PopoverContent>
										</Popover>

										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="mt-9 flex justify-end">
							<Button
								type="submit"
								disabled={!form.formState.isValid || form.formState.isSubmitting}
							>
								{form.formState.isSubmitting ? 'Updating...' : 'Update'}
							</Button>
						</div>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
}
